<ng-template #headerTemplate>
  <ng-container *ngIf="!forumId && enableNewForumExperience && selectedFeedType; else defaultHeader">
    <div class="d-inline-block" ngbDropdown>
      <button
        id="feedTypeDropdown"
        class="btn btn-link dropdown-toggle p-0 text-decoration-none fw-bold"
        ngbDropdownToggle
        type="button"
      >
        {{ getCurrentFeedLabel() }}
      </button>
      <div class="dropdown-menu" aria-labelledby="feedTypeDropdown" ngbDropdownMenu>
        <button
          *ngIf="isAuthenticated"
          [class.active]="selectedFeedType === ForumTabType.MY_FEED"
          (click)="changeFeedType(ForumTabType.MY_FEED)"
          class="dropdown-item"
          type="button"
        >
          <fa-icon [icon]="['fas', 'rss']" class="me-2"></fa-icon>
          <div>
            <div>{{ "My forum feed" | translate }}</div>
            <small class="text-muted">{{ "A curated list of topics that you can customize." | translate }}</small>
          </div>
        </button>
        <button
          [class.active]="selectedFeedType === ForumTabType.ACTIVE"
          (click)="changeFeedType(ForumTabType.ACTIVE)"
          class="dropdown-item"
          type="button"
        >
          <fa-icon [icon]="['fas', 'bolt']" class="me-2"></fa-icon>
          <div>
            <div>{{ "Active forum topics" | translate }}</div>
            <small class="text-muted">{{ "Topics with a high activity at the moment." | translate }}</small>
          </div>
        </button>
        <button
          [class.active]="selectedFeedType === ForumTabType.NEW"
          (click)="changeFeedType(ForumTabType.NEW)"
          class="dropdown-item"
          type="button"
        >
          <fa-icon [icon]="['fas', 'star']" class="me-2"></fa-icon>
          <div>
            <div>{{ "New forum topics" | translate }}</div>
            <small class="text-muted">{{ "Topics that were recently started." | translate }}</small>
          </div>
        </button>
        <button
          [class.active]="selectedFeedType === ForumTabType.LATEST"
          (click)="changeFeedType(ForumTabType.LATEST)"
          class="dropdown-item"
          type="button"
        >
          <fa-icon [icon]="['fas', 'clock']" class="me-2"></fa-icon>
          <div>
            <div>{{ "Latest from the forums" | translate }}</div>
            <small class="text-muted">{{ "Topics with recent replies." | translate }}</small>
          </div>
        </button>
      </div>
    </div>
  </ng-container>
  <ng-template #defaultHeader>
    {{ "Recent forum posts" | translate }}
  </ng-template>
</ng-template>

<ng-template #footerTemplate>
  <ng-container *ngIf="!loading; else loadingTemplate">
    <a *ngIf="topics?.length > MAX_TOPICS" [href]="forumUrl" class="btn btn-secondary">
      {{ "View all" | translate }}
    </a>

    <a [href]="forumNewTopicUrl" class="btn btn-primary">
      {{ "New topic" | translate }}
    </a>
  </ng-container>
</ng-template>

<ng-template #tableTemplate>
  <ng-container *ngIf="!loading && topics !== null; else loadingTemplate">
    <table *ngIf="topics.length > 0; else noTopicsTemplate" [class.table-striped]="striped" class="table mb-0">
      <tbody>
        <tr *ngFor="let topic of topics; trackBy: topicTrackByFn" [class.loading]="loadingTopicId === topic.id">
          <td class="avatar-cell d-none d-md-table-cell">
            <a [routerLink]="['/u', topic.username]">
              <img
                [ngSrc]="topic.userAvatar | orDefaultAvatar"
                class="user-avatar"
                alt="{{ topic.displayName || topic.username }}"
                height="40"
                loading="lazy"
                width="40"
              />
            </a>
          </td>

          <td class="title">
            <a
              *ngIf="!enableNewForumExperience"
              [class.read]="!isAuthenticated || topic.read"
              [class.unread]="isAuthenticated && !topic.read"
              [href]="topicUrl(topic.id)"
            >
              {{ topic.name }}
            </a>
            <a
              *ngIf="enableNewForumExperience"
              [class.read]="!isAuthenticated || topic.read"
              [class.unread]="isAuthenticated && !topic.read"
              [href]="topicUrl(topic.id)"
              (click)="onTopicClick($event, topic)"
            >
              {{ topic.name }}
            </a>

            <div class="meta">
              <!-- Tags row (only show if enableNewForumExperience and tags exist) -->
              <div *ngIf="enableNewForumExperience && getAcceptedTags(topic.tags)?.length" class="pills-row">
                <span class="tags-container">
                  <ng-container *ngFor="let tag of getAcceptedTags(topic.tags).slice(0, 3)">
                    <span
                      [class.suggested-tag]="tag.status === 'suggested'"
                      [style.--tag-border-color]="tag.status !== 'suggested' ? (tag | tagBorderColor) : null"
                      [style.border]="tag.status !== 'suggested' ? '1px solid ' + (tag | tagBorderColor) : null"
                      [style.color]="tag.status !== 'suggested' ? (tag | tagTextColor) : null"
                      class="tag-pill"
                    >
                      <fa-icon *ngIf="tag.icon && tag.status !== 'suggested'" [icon]="tag.icon" class="me-1"></fa-icon>
                      {{ tag.name }}
                    </span>
                  </ng-container>
                  <span *ngIf="getAcceptedTags(topic.tags).length > 3" class="tag-pill more-tags">
                    +{{ getAcceptedTags(topic.tags).length - 3 }}
                  </span>
                </span>
              </div>

              <!-- User and timestamp row with inline pills -->
              <div class="user-timestamp-row">
                <!-- Group pill inline -->
                <span *ngIf="topic.groupName" class="group-pill me-2">
                  <fa-icon [icon]="['fas', 'users']" class="me-1"></fa-icon>
                  {{ topic.groupName }}
                </span>

                <!-- Forum pill inline -->
                <span
                  *ngIf="!enableNewForumExperience && topic.forumName && !topic.groupName && !forumId"
                  class="forum-pill me-2"
                >
                  {{ topic.forumName }}
                </span>

                <abbr [attr.title]="topic.created | localDate">
                  {{ topic.created | localDate | timeagoWithFormat: translateService.currentLang : "short" }}
                </abbr>
                <span class="separator">·</span>
                <a [routerLink]="['/u', topic.username]">{{ topic.displayName || topic.username }}</a>

                <!-- Last poster info on same line (only show if topic has multiple posts) -->
                <ng-container *ngIf="topic.lastPostUsername && topic.postCount > 1">
                  <span class="separator">·</span>
                  <ng-container
                    *ngIf="
                      currentUserProfile && topic.lastPostUsername === currentUserProfile.username;
                      else showLastPosterName
                    "
                  >
                    <span class="last-post-label">{{ "Last post by you." | translate }}</span>
                  </ng-container>
                  <ng-template #showLastPosterName>
                    <span class="last-post-label">{{ "Last post" | translate }}:</span>
                    <a [routerLink]="['/u', topic.lastPostUsername]" class="user-link ms-1">
                      {{ topic.lastPostUserDisplayName || topic.lastPostUsername }}
                    </a>
                  </ng-template>
                </ng-container>
              </div>
            </div>
          </td>

          <td class="stats d-none d-md-table-cell">
            <div class="stats-container">
              <div class="stat-item">
                <fa-icon icon="comments"></fa-icon>
                <span>{{ topic.postCount - 1 }}</span>
              </div>
              <div class="stat-item">
                <fa-icon icon="eye"></fa-icon>
                <span>{{ topic.views }}</span>
              </div>
              <div *ngIf="topic.lastPostTimestamp" class="stat-item last-update">
                <fa-icon [icon]="['far', 'clock']"></fa-icon>
                <abbr [attr.title]="topic.lastPostTimestamp | localDate">
                  {{ topic.lastPostTimestamp | localDate | timeagoWithFormat: translateService.currentLang : "short" }}
                </abbr>
              </div>
            </div>
          </td>

          <td *ngIf="!enableNewForumExperience" class="go-to-last-post">
            <a
              *ngIf="topic.lastPostId"
              [href]="classicRoutesService.FORUM_POST(topic.lastPostId.toString())"
              class="p-3"
            >
              &rsaquo;
            </a>
          </td>
        </tr>
      </tbody>
    </table>
  </ng-container>
</ng-template>

<ng-template #loadingTemplate>
  <astrobin-loading-indicator></astrobin-loading-indicator>
</ng-template>

<ng-template #noTopicsTemplate>
  <ng-container *ngIf="!!forumId">
    <p>
      <span>
        {{ "There are no topics in this forum." | translate }}
      </span>
      <span>
        <a [href]="forumNewTopicUrl">
          {{ "Want to be the first to post?" | translate }}
        </a>
      </span>
    </p>

    <p *ngIf="category?.slug === 'equipment-forums'" class="text-muted">
      {{
        "When you start a new discussions here, everybody who has used this equipment item on AstroBin will be notified " +
          "unless they opted-out." | translate
      }}
    </p>
  </ng-container>

  <ng-container *ngIf="!forumId">
    <p>
      {{ "There are no recent topics." | translate }}
    </p>
  </ng-container>
</ng-template>

<ng-container *ngIf="useCard">
  <div class="card">
    <div *ngIf="showHeader" class="card-header">
      <ng-container [ngTemplateOutlet]="headerTemplate"></ng-container>
    </div>

    <div class="card-body">
      <ng-container [ngTemplateOutlet]="tableTemplate"></ng-container>
    </div>

    <div *ngIf="showFooter" class="card-footer">
      <ng-container [ngTemplateOutlet]="footerTemplate"></ng-container>
    </div>
  </div>
</ng-container>

<ng-container *ngIf="!useCard">
  <h4 *ngIf="showHeader">
    <ng-container [ngTemplateOutlet]="headerTemplate"></ng-container>
  </h4>

  <ng-container [ngTemplateOutlet]="tableTemplate"></ng-container>

  <div *ngIf="showFooter">
    <ng-container [ngTemplateOutlet]="footerTemplate"></ng-container>
  </div>
</ng-container>

<div
  *ngIf="category?.slug === 'equipment-forums' && topics?.length > 0"
  [innerHTML]="equipmentCategoryDescription"
  class="category-description"
></div>
